import { Component } from '@theme/component';

/**
 * SD Slider Component
 *
 * A modern, performant slider using CSS scroll-snap with best practices:
 * - IntersectionObserver for efficient visibility tracking
 * - AbortController for clean event cleanup
 * - CSS scroll-snap for native smooth scrolling
 * - Touch/pointer drag support
 * - Autoplay with visibility and hover awareness
 * - Reduced motion support
 *
 * @typedef {Object} SDSliderRefs
 * @property {HTMLElement} scroller - Scrollable container
 * @property {HTMLElement} slidesContainer - Container for slides
 * @property {HTMLElement[]} slide - Array of slide elements
 * @property {HTMLButtonElement[]} [dot] - Array of dot navigation buttons
 * @property {HTMLButtonElement} [prev] - Previous button
 * @property {HTMLButtonElement} [next] - Next button
 * @property {HTMLElement} [liveRegion] - ARIA live region for announcements
 *
 * @extends {Component<SDSliderRefs>}
 */
class SDSlider extends Component {
  requiredRefs = ['scroller'];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {IntersectionObserver|null} */
  #intersectionObserver = null;

  /** @type {number|null} */
  #autoplayTimer = null;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #isPaused = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const slides = this.refs.slide;
    if (!this.refs.scroller || !slides?.length) return;

    // Skip autoplay and complex interactions if user prefers reduced motion
    const prefersReducedMotion = this.#prefersReducedMotion();

    this.#setupIntersectionObserver();
    this.#setupEventListeners(signal);

    // Only setup drag handling if motion is allowed
    if (!prefersReducedMotion) {
      this.#setupDragHandling(signal);
    }

    // Initialize button states
    this.#setCurrentIndex(0);

    // Start autoplay if enabled and motion is allowed
    if (this.autoplayInterval && !prefersReducedMotion) {
      this.play();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#stopAutoplay();
  }

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get autoplay interval in milliseconds
   * @returns {number|null}
   */
  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    if (!interval) return null;
    const value = parseInt(interval, 10);
    return isNaN(value) ? null : value * 1000;
  }

  /**
   * Check if infinite loop is enabled
   * @returns {boolean}
   */
  get infinite() {
    return this.hasAttribute('infinite');
  }

  /**
   * Check if arrow direction is reversed
   * @returns {boolean}
   */
  get reverseArrows() {
    return this.hasAttribute('reverse-arrows');
  }

  /**
   * Get current slide index
   * @returns {number}
   */
  get currentIndex() {
    return this.#currentIndex;
  }

  // ==========================================================================
  // Public Methods
  // ==========================================================================

  /**
   * Navigate to a specific slide
   * @param {number} index - Target slide index
   */
  select(index) {
    const slides = this.refs.slide;
    if (!slides?.length) return;

    // Ensure announcements are enabled for manual navigation
    this.refs.liveRegion?.setAttribute('aria-live', 'polite');

    // Handle infinite loop
    let targetIndex = index;
    if (this.infinite) {
      if (index < 0) targetIndex = slides.length - 1;
      else if (index >= slides.length) targetIndex = 0;
    } else {
      targetIndex = Math.max(0, Math.min(index, slides.length - 1));
    }

    const targetSlide = slides[targetIndex];
    if (!targetSlide) return;

    // Scroll to slide using CSS scroll-snap
    const behavior = this.#prefersReducedMotion() ? 'auto' : 'smooth';
    targetSlide.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: 'start',
    });

    this.#setCurrentIndex(targetIndex);
  }

  /**
   * Go to next slide
   * When reverse-arrows is enabled, this moves backward
   */
  next() {
    if (this.reverseArrows) {
      this.select(this.#currentIndex - 1);
    } else {
      this.select(this.#currentIndex + 1);
    }
  }

  /**
   * Go to previous slide
   * When reverse-arrows is enabled, this moves forward
   */
  previous() {
    if (this.reverseArrows) {
      this.select(this.#currentIndex + 1);
    } else {
      this.select(this.#currentIndex - 1);
    }
  }

  /**
   * Start autoplay
   */
  play() {
    if (!this.autoplayInterval || this.#autoplayTimer || this.#prefersReducedMotion()) return;

    this.#isPaused = false;
    // Set aria-live to 'off' during autoplay to prevent announcement overload
    this.refs.liveRegion?.setAttribute('aria-live', 'off');

    this.#autoplayTimer = setInterval(() => {
      // Don't advance if document is hidden or user is hovering
      if (document.hidden || this.matches(':hover')) return;
      // Always advance forward for autoplay, regardless of reverse-arrows setting
      this.select(this.#currentIndex + 1);
    }, this.autoplayInterval);
  }

  /**
   * Pause autoplay
   */
  pause() {
    this.#isPaused = true;
    this.#stopAutoplay();
    // Set aria-live to 'polite' when paused to allow announcements
    this.refs.liveRegion?.setAttribute('aria-live', 'polite');
  }

  /**
   * Resume autoplay if not explicitly paused
   */
  resume() {
    if (this.#isPaused || this.#prefersReducedMotion()) return;
    this.play();
  }

  // ==========================================================================
  // Private Methods
  // ==========================================================================

  /**
   * Check if user prefers reduced motion
   * @returns {boolean}
   */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Setup IntersectionObserver to track visible slides
   */
  #setupIntersectionObserver() {
    const slides = this.refs.slide;
    if (!slides?.length) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const slide = entry.target;
            const index = slides.indexOf(slide);
            if (index !== -1 && index !== this.#currentIndex) {
              this.#setCurrentIndex(index);
            }
          }
        }
      },
      {
        root: this.refs.scroller,
        threshold: 0.5,
        rootMargin: '0px',
      }
    );

    slides.forEach((slide) => this.#intersectionObserver?.observe(slide));
  }

  /**
   * Setup event listeners
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #setupEventListeners(signal) {
    const { prev, next, dot } = this.refs;

    // Navigation buttons
    prev?.addEventListener('click', () => this.previous(), { signal });
    next?.addEventListener('click', () => this.next(), { signal });

    // Dots
    dot?.forEach((dotEl) => {
      dotEl.addEventListener(
        'click',
        () => {
          const index = parseInt(dotEl.dataset.slideIndex || '0', 10);
          this.select(index);
        },
        { signal }
      );
    });

    // Autoplay controls
    if (this.autoplayInterval) {
      // Pause on hover
      this.addEventListener('mouseenter', () => this.#stopAutoplay(), { signal });
      this.addEventListener('mouseleave', () => this.resume(), { signal });

      // Pause when tab is hidden
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.#stopAutoplay();
          } else {
            this.resume();
          }
        },
        { signal }
      );
    }

    // Keyboard navigation
    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.previous();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        }
      },
      { signal }
    );
  }

  /**
   * Setup drag/swipe handling
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #setupDragHandling(signal) {
    const slides = this.refs.slide;
    const scroller = this.refs.scroller;
    if (!slides?.length || !scroller) return;

    let startX = 0;
    let startScrollLeft = 0;
    /** @type {AbortController|null} */
    let dragController = null;

    /**
     * @param {PointerEvent} e
     */
    const onPointerDown = (e) => {
      if (slides.length <= 1) return;
      if (e.target instanceof Element && e.target.closest('a, button')) return;

      this.#isDragging = true;
      this.setAttribute('dragging', '');
      startX = e.pageX;
      startScrollLeft = scroller.scrollLeft;

      // Create controller for drag events
      dragController = new AbortController();
      const dragSignal = dragController.signal;

      document.addEventListener('pointermove', onPointerMove, { signal: dragSignal });
      document.addEventListener('pointerup', onPointerUp, { signal: dragSignal });
      document.addEventListener('pointercancel', onPointerUp, { signal: dragSignal });

      // Pause autoplay during drag
      this.#stopAutoplay();
    };

    /**
     * @param {PointerEvent} e
     */
    const onPointerMove = (e) => {
      if (!this.#isDragging) return;
      e.preventDefault();

      const x = e.pageX;
      const walk = (startX - x) * 1.5; // Drag sensitivity
      scroller.scrollLeft = startScrollLeft + walk;
    };

    const onPointerUp = () => {
      this.#isDragging = false;
      this.removeAttribute('dragging');
      dragController?.abort();
      dragController = null;

      // Resume autoplay after drag
      this.resume();
    };

    scroller.addEventListener('pointerdown', onPointerDown, { signal });
  }

  /**
   * Update current index and sync UI
   * @param {number} index - New current index
   */
  #setCurrentIndex(index) {
    const slides = this.refs.slide;
    const dots = this.refs.dot;
    const { prev, next, liveRegion } = this.refs;

    this.#currentIndex = index;

    // Update aria-hidden on slides
    slides?.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
    });

    // Update dots (using aria-current instead of aria-selected)
    dots?.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    // Update button states for non-infinite mode
    // When reverse-arrows is enabled, swap the disabled states
    if (!this.infinite && slides?.length) {
      const atStart = index === 0;
      const atEnd = index === slides.length - 1;

      if (this.reverseArrows) {
        if (prev) prev.disabled = atEnd;
        if (next) next.disabled = atStart;
      } else {
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
      }
    }

    // Announce to screen readers
    if (liveRegion && slides?.length) {
      liveRegion.textContent = `Slide ${index + 1} of ${slides.length}`;
    }
  }

  /**
   * Stop autoplay timer
   */
  #stopAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }
}

// Register custom element
if (!customElements.get('sd-slider-component')) {
  customElements.define('sd-slider-component', SDSlider);
}
